import { resolveMarketingSiteOrigin } from './absoluteSiteUrl.js';
import type { PublicMarketingConfig, RobotsAiCrawlerPolicy } from './publicMarketingConfig.js';

export type { RobotsAiCrawlerPolicy };

/** Paths disallowed for all tenant marketing sites (SaaS app internals). */
export const TENANT_ROBOTS_DISALLOW_PATHS = [
  '/admin',
  '/login',
  '/reset-password',
  '/auth/',
  '/api/',
  '/sandbox-admin/',
  '/quote/',
  '/m/',
  '/create-account',
  '/book-your-service',
  '/clear-cache',
  '/clear-cache.html',
  '/clear-cache-mobile.html',
  '/clear-auth.html',
  '/logout.html',
  '/generate-icon.html',
  '/generate-icons.html',
  '/generate-pwa-icons.html',
  '/404.html',
] as const;

/** Recommended explicit allow paths for marketing crawlers (optional platform/org extras). */
export const SAAS_DEFAULT_ROBOTS_ALLOW_PATHS = ['/blog', '/llms.txt', '/guides/'] as const;

/** Default AI training / retrieval crawlers blocked when policy is disallow. */
export const SAAS_AI_CRAWLER_USER_AGENTS = [
  'GPTBot',
  'ChatGPT-User',
  'OAI-SearchBot',
  'PerplexityBot',
  'ClaudeBot',
  'anthropic-ai',
  'Google-Extended',
  'GoogleOther',
  'CCBot',
  'cohere-ai',
  'Meta-ExternalAgent',
  'FacebookBot',
] as const;

function normalizeRobotsPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

function mergeRobotsPathLayers(...layers: Array<string[] | undefined>): string[] {
  const merged = new Set<string>();
  for (const layer of layers) {
    for (const path of layer ?? []) {
      const normalized = normalizeRobotsPath(path);
      if (normalized) merged.add(normalized);
    }
  }
  return [...merged];
}

/** Merge SaaS base disallow paths with org/platform extras. */
export function resolveRobotsDisallowPaths(
  config?: Pick<PublicMarketingConfig, 'robotsDisallowPaths'>,
): string[] {
  const extra = config?.robotsDisallowPaths ?? [];
  return [...new Set([...TENANT_ROBOTS_DISALLOW_PATHS, ...mergeRobotsPathLayers(extra)])];
}

/** Resolve allow paths; always includes `/` plus platform/org extras. */
export function resolveRobotsAllowPaths(config?: Pick<PublicMarketingConfig, 'robotsAllowPaths'>): string[] {
  const extras = config?.robotsAllowPaths ?? [];
  return [...new Set(['/', ...mergeRobotsPathLayers(extras)])];
}

export function resolveRobotsAiCrawlerPolicy(
  config?: Pick<PublicMarketingConfig, 'robotsAiCrawlerPolicy'>,
): RobotsAiCrawlerPolicy {
  return config?.robotsAiCrawlerPolicy === 'disallow' ? 'disallow' : 'allow';
}

/** User-agents blocked when AI crawler policy is disallow (org overrides platform overrides SaaS defaults). */
export function resolveBlockedAiCrawlerUserAgents(
  config?: Pick<PublicMarketingConfig, 'robotsAiCrawlerPolicy' | 'robotsAiCrawlerUserAgents'>,
): string[] {
  if (resolveRobotsAiCrawlerPolicy(config) !== 'disallow') return [];
  const custom = (config?.robotsAiCrawlerUserAgents ?? []).map((agent) => agent.trim()).filter(Boolean);
  if (custom.length > 0) return [...new Set(custom)];
  return [...SAAS_AI_CRAWLER_USER_AGENTS];
}

export function buildAiCrawlerRobotsBlocks(userAgents: readonly string[]): string {
  if (!userAgents.length) return '';
  return userAgents.map((agent) => `User-agent: ${agent}\nDisallow: /`).join('\n\n');
}

/** Resolve canonical site origin for tenant robots.txt / sitemap directives. */
export function resolveTenantRobotsSiteOrigin(websiteUrl: string | undefined, requestHost: string): string {
  const trimmed = websiteUrl?.trim();
  if (trimmed) {
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    return resolveMarketingSiteOrigin(withProtocol);
  }
  const host = requestHost.replace(/:\d+$/, '').trim();
  return host ? `https://${host}` : resolveMarketingSiteOrigin();
}

/** Build robots.txt for a tenant marketing site on a custom domain. */
export function buildTenantRobotsTxt(
  siteOrigin: string,
  disallowPaths: readonly string[] = TENANT_ROBOTS_DISALLOW_PATHS,
  allowPaths: readonly string[] = ['/'],
  blockedAiCrawlers: readonly string[] = [],
): string {
  const origin = siteOrigin.replace(/\/$/, '');
  const allow = allowPaths.map((path) => `Allow: ${path}`).join('\n');
  const disallow = disallowPaths.map((path) => `Disallow: ${path}`).join('\n');
  const aiBlocks = buildAiCrawlerRobotsBlocks(blockedAiCrawlers);
  const aiSection = aiBlocks ? `\n\n${aiBlocks}` : '';

  return `# Tenant marketing robots.txt (generated)
User-agent: *
${allow}

${disallow}
${aiSection}

Sitemap: ${origin}/sitemap.xml
`;
}

/** Preview tenant robots.txt from resolved public marketing config and branding. */
export function buildTenantRobotsTxtPreview(
  config: Pick<
    PublicMarketingConfig,
    'robotsDisallowPaths' | 'robotsAllowPaths' | 'robotsAiCrawlerPolicy' | 'robotsAiCrawlerUserAgents'
  >,
  websiteUrl?: string,
  requestHost = 'tenant.example.com',
): string {
  const origin = resolveTenantRobotsSiteOrigin(websiteUrl, requestHost);
  return buildTenantRobotsTxt(
    origin,
    resolveRobotsDisallowPaths(config),
    resolveRobotsAllowPaths(config),
    resolveBlockedAiCrawlerUserAgents(config),
  );
}
